import useLocalStorage from '../useLocalStorage'
import { urlLocaleRegex } from './helpers'
import type { RouteLocation } from '#vue-router'
import { normalizeRouteKey } from '~/utils/routeNormalization'

type IPromotionItemKeys = 'promotion_name' | 'promotion_id' | 'location_id'
type IPromotionItem = Pick<PromotionItem, IPromotionItemKeys>
interface IPromotionAdditionalKeys {
  item_category?: string
  creative_name?: string
  creative_slot?: string
  creative_name_storyblok?: string
}
export type IPromotionKeys = IPromotionItem & IPromotionAdditionalKeys

interface IPromotionsStorageFormat {
  // key is product ID
  [key: string]: Partial<IPromotionKeys>
}

interface IBannersStorageFormat {
  // key is banner target url
  [key: string]: Partial<IPromotionKeys>
}

interface IRoutesStorageFormat {
  // key is banner target url
  [key: string]: ActivePromotionData
}

interface IPromotionTrackingStorageFormat {
  // stores all clicked promoted products
  promotions: IPromotionsStorageFormat
  // stores all clicked banners that did not contain any product
  banners: IBannersStorageFormat,
  routes: IRoutesStorageFormat
}

const defaultPromotionTrackingStore = {
  promotions: {},
  banners: {},
  routes: {},
}

const { stored, set } = useLocalStorage<IPromotionTrackingStorageFormat>(
  'promotionTracking',
  defaultPromotionTrackingStore,
)

const usePromotionTrackingLocalStorage = () => {
  const promotions = computed(() => stored.value.promotions)
  const banners = computed(() => stored.value.banners)
  const routes = computed(() => stored.value.routes)

  const getProductPromotion = (itemId: string) => {
    return promotions.value?.[itemId]
  }

  const setProductPromotion = (itemId: string, promotion: ActivePromotionData) => {
    set({
      promotions: {
        ...promotions.value,
        [itemId]: promotion,
      },
      banners: banners.value,
      routes: routes.value,
    })
  }

  const deleteProductPromotions = (itemIds: string[]) => {
    const promotionsCopy = { ...promotions.value }

    for (let i = 0; i < itemIds.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete promotionsCopy[itemIds[i]]
    }

    set({
      promotions: promotionsCopy,
      banners: banners.value,
      routes: routes.value,
    })
  }

  /**
   * @deprecated URL-keyed banner lookup is brittle (locale/query variance). Prefer activePromotion + per-product mapping.
   */
  const getStoredBanner = (url: string, itemId: string) => {
    // PromotionItem keys url are stored on localStorage with locale,
    // and sometimes url string supplied isn't given with locale
    const bannerKey =
      !urlLocaleRegex.test(url) && url !== '/'
        ? Object.keys(banners.value).find((i) => i.includes(url))
        : url
    const banner = bannerKey ? banners.value?.[bannerKey] : undefined

    // stores the PromotionItem with product ID for subsequent ecommerce events
    if (banner) {
      setProductPromotion(itemId, banner as ActivePromotionData)
    }
    return banner
  }

  /**
   * @deprecated Avoid writing URL-keyed banners. Use setActivePromotion on click and assign per product.
   */
  const storeBanner = (value: IBannersStorageFormat) => {
    set({
      promotions: promotions.value,
      banners: {
        ...banners.value,
        ...value,
      },
      routes: routes.value,
    })
  }

  const getRoutePromotion = (route: RouteLocation) => {
    const key = normalizeRouteKey(route.path, [])
    return routes.value?.[key]
  }

  const setRoutePromotion = (route: RouteLocation, promotion: ActivePromotionData) => {
    const key = normalizeRouteKey(route.path, [])
    const routePromotion = { [key]: promotion }

    set({
      promotions: promotions.value,
      banners: banners.value,
      routes: {
        ...routes.value,
        ...routePromotion,
      },
    })
  }

  /**
   * @deprecated Legacy cleanup for URL-keyed banners.
   */
  const deleteBanners = (urls: string[]) => {
    const bannersCopy = { ...banners.value }

    for (let i = 0; i < urls.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete bannersCopy[urls[i]]
    }

    set({
      promotions: promotions.value,
      banners: bannersCopy,
      routes: routes.value,
    })
  }

  return {
    getProductPromotion,
    setProductPromotion,
    deleteProductPromotions,
    getStoredBanner,
    storeBanner,
    deleteBanners,
    getRoutePromotion,
    setRoutePromotion,
  }
}

export default usePromotionTrackingLocalStorage
